import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import moment from 'moment';
import { message } from 'antd';
import {
  getDeliveries,
  editDelivery,
  listPOF,
  deletePOF,
  fetchBranchMembers,
  fetchUserDetails,
  fetchVendors,
  editPurchaseOrder,
} from '../deliveryApi';

export const useDeliveryList = () => {
  const [data, setData] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total_pos, setTotalPos] = useState<number>(0);
  const [search, setSearch] = useState("");
  const [po_data, setPOData] = useState<any>(null);

  const [members, setMembers] = useState([]);
  const [activeTab, setActiveTab] = useState<string>("express");
  
  const [created_at__gte, setCreated_at__gte] = useState<string>();
  const [created_at__lte, setCreated_at__lte] = useState<string>();
  const [dispatch_time__gte, setDispatchTime__gte] = useState<string | undefined>();
  const [dispatch_time__lte, setDispatchTime__lte] = useState<string | undefined>();
  const [deliveryTime__gte, setDeliveryTime__gte] = useState<string | undefined>();
  const [deliveryTime__lte, setDeliveryTime__lte] = useState<string | undefined>();
  
  const [created_by, setCreated_by] = useState<string>();
  const [vendor_name, setVendor_name] = useState<string>();
  const [tab_type, setTabType] = useState<string | null>(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [tripsDrawerVisible, setTripsDrawerVisible] = useState(false);
  const deliveriesView = activeTab === "deliveries";
  const outstationView = activeTab === "outstation";
  const etasView = activeTab === "delivery_assigned";
  
  const [attachDeliveryDrawerVisible, setAttachDeliveryDrawerVisible] = useState<number | null>(null);
  const [attachPODDrawerVisible, setAttachPODDrawerVisible] = useState<any | null>(null);
  const [attachLRDrawerVisible, setAttachLRDrawerVisible] = useState<any | null>(null);
  const [warehouseDrawerVisible, setWarehouseDrawerVisible] = useState(false);
  const [showLoginextCSVUpload, setShowLoginextCSVUpload] = useState(false);
  const [delivery_location_drawer_visible, setDeliveryLocationDrawerVisible] = useState(false);
  const [eway_bill_drawer_visible, setEwayBillDrawerVisible] = useState(false);

  const [currentUser, setCurrentUser] = useState<Record<string, unknown> | null>(null);
  const [vendors, setVendors] = useState<Array<any>>([]);
  const [vendor_id, setVendorId] = useState<any>(null);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [deliveriesCount, setDeliveriesCount] = useState<number>(0);
  const [deliveriesPage, setDeliveriesPage] = useState<number>(1);
  const [itemsToView, setItemsToView] = useState<any[]>([]);
  const [sortVal, setSortVal] = useState<string>("created_at");
  const [pofData, setPofData] = useState<Record<number, any>>({});
  const [etasSearch, setEtasSearch] = useState('');
  const [porterLinkDrawerData, setPorterLinkDrawerData] = useState<any>(null);
  const [dispatchTimeDrawerVisible, setDispatchTimeDrawerVisible] = useState<any>(null);
  const [escalationDrawerData, setEscalationDrawerData] = useState<any>(null);
  const [city, setCity] = useState<string>("All");

  // Store current search filters for pagination
  const [currentSearchFilter, setCurrentSearchFilter] = useState<{ search?: string } | null>(null);

  // Refs for request cancellation and debouncing
  const abortControllerRef = useRef<AbortController | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const skipNextFetchRef = useRef<boolean>(false);
  const activeTabRef = useRef<string>(activeTab);

  const handleReset = () => {
    setCreated_by(undefined);
    setCreated_at__gte(undefined);
    setCreated_at__lte(undefined);
    setVendor_name(undefined);
    setDispatchTime__gte(undefined);
    setDispatchTime__lte(undefined);
    setDeliveryTime__gte(undefined);
    setDeliveryTime__lte(undefined);
    setCurrentSearchFilter(null);
    setSearch("");
    setCity("All");
  };

  // Keep activeTabRef in sync so the debounced handler can read current tab without stale closure
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // Debounced search handler — contains match on both PO number and Lead ID.
  // Results are always shown under the Leftovers tab (except Delivery Assigned which is unchanged).
  const handleSearchChange = useCallback(async (value: string) => {
    setSearch(value);
    const trimmed = value.trim();

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (trimmed.length >= 3) {
        setLoading(true);
        const deliveryInfo = await getDeliveries({ search: trimmed });
        if (deliveryInfo) {
          if (deliveryInfo.results) {
            setData(deliveryInfo.results);
            setTotalPos(deliveryInfo.count ?? 0);
          } else if (!Array.isArray(deliveryInfo)) {
            setData([deliveryInfo]);
            setTotalPos(1);
          }
          setCurrentSearchFilter({ search: trimmed });
          if (activeTabRef.current !== "delivery_assigned") {
            if (activeTabRef.current !== "leftovers") {
              skipNextFetchRef.current = true;
            }
            setActiveTab("leftovers");
          }
        }
        setLoading(false);
      }
    }, 300);
  }, []);

  const getVendorsList = useCallback(() => {
    // Only fetch vendors if search is meaningful
    if (search && search.length > 2) {
      fetchVendors({ search }).then((res) => {
        setVendors(res);
      });
    }
  }, [search]);

  const tabParams = useMemo(() => {
    const today = moment().format("YYYY-MM-DD");
    const todayStart = today + "T00:00:00Z";
    const tomorrowStart = moment().add(1, "day").format("YYYY-MM-DD") + "T00:00:00Z";
    switch (activeTab) {
      case "express":
        return { is_express: true, leg_commited_dispatch_time__gte: todayStart, leg_commited_dispatch_time__lte: tomorrowStart, leg_status_in: "dispatch_pending,in_warehouse,in_store,pickup_attempted" };
      case "delivery_assigned": {
        const params: Record<string, any> = {
          leg_status_in: "delivery_assigned,dispatched",
          ordering: "trip_name",
          page_size: 500,  // Fetch more data to get complete trips
        };
        if (!dispatch_time__gte && !dispatch_time__lte) {
          params.leg_commited_dispatch_time__gte = todayStart;
          params.leg_commited_dispatch_time__lte = tomorrowStart;
        }
        return params;
      }
      case "outstation":
        return { is_outstation: true, leg_commited_dispatch_time__gte: todayStart, leg_commited_dispatch_time__lte: tomorrowStart, leg_status_in: "dispatch_pending,in_warehouse,in_store,pickup_attempted" };
      case "leftovers":
        return { leg_commited_dispatch_time__gte: todayStart, leg_commited_dispatch_time__lte: tomorrowStart, leg_status_in: "dispatch_pending,pickup_attempted,delivery_attempted", is_express: false, is_outstation: false };
      case "dispatched":
        return { leg_status_in: "dispatched", leg_commited_dispatch_time__gte: todayStart, leg_commited_dispatch_time__lte: tomorrowStart };
      case "spill_overs":
        return {
          leg_commited_dispatch_time__lte: todayStart,
          leg_status_in: "dispatch_pending,delivery_assigned,dispatched,pickup_attempted,delivery_attempted",
        };
      case "deliveries":
        return { last_updated_status: "loginext_pushed" };
      default:
        return {};
    }
  }, [activeTab, dispatch_time__gte, dispatch_time__lte]);

  const queryParams = useMemo(() => {
    const params: any = {
      ...tabParams,
      ordering: sortVal,
    };
    if (city && city !== "All") {
      params.city = city;
    }
    return params;
  }, [tabParams, sortVal, city]);

  const fetchDeliveries = useCallback(async (paramsOverride: any = {}) => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    const pageToFetch = paramsOverride.page || page;
    setPage(pageToFetch);

    // search key present + non-empty → new search
    // search key present + empty/blank → explicit clear (go back to tab data)
    // search key absent → pagination or tab refresh (respect currentSearchFilter)
    const rawSearch = paramsOverride.search;
    const trimmedSearch = rawSearch?.trim() ?? null;
    const isSearch = !!trimmedSearch;
    const isClearSearch = rawSearch !== undefined && !trimmedSearch;
    const keepCurrentSearch = !!paramsOverride.keepCurrentSearch;

    if (isSearch) {
      setCurrentSearchFilter({ search: trimmedSearch! });
    } else if (isClearSearch || (!keepCurrentSearch && !paramsOverride.page)) {
      setCurrentSearchFilter(null);
    }

    // Determine which params to use
    let params: any;
    if (isSearch) {
      params = { search: trimmedSearch, page: pageToFetch };
    } else if (!isClearSearch && currentSearchFilter?.search) {
      // Pagination or keepCurrentSearch while a search filter is active
      params = { search: currentSearchFilter.search, page: pageToFetch };
    } else {
      params = {
        ...queryParams,
        page: pageToFetch,
      };
    }

    try {
      const response = await getDeliveries(params);

      // Only update state if request wasn't aborted
      if (!abortController.signal.aborted) {
        // Handle both paginated response (with results array) and single object response
        if (response?.results) {
          // Filter out unassigned deliveries for delivery_assigned tab
          const filteredResults = activeTab === "delivery_assigned"
            ? response.results.filter((item: any) => item.trip_name)
            : response.results;
          setData(filteredResults);
          setTotalPos(response.count ?? filteredResults.length);
        } else if (response && !Array.isArray(response)) {
          // Single object response (e.g., when searching by po_number)
          setData([response]);
          setTotalPos(1);
        } else {
          setData([]);
          setTotalPos(0);
        }
        setLoading(false);
        // Switch to Leftovers tab when search is active (except Delivery Assigned)
        if (isSearch && activeTab !== "delivery_assigned") {
          if (activeTab !== "leftovers") {
            skipNextFetchRef.current = true;
          }
          setActiveTab("leftovers");
        }
      }
    } catch (error) {
      // Only handle error if request wasn't aborted
      if (!abortController.signal.aborted) {
        console.error('Error fetching deliveries:', error);
        setLoading(false);
      }
    }
  }, [page, queryParams, created_at__gte, created_at__lte, activeTab, currentSearchFilter]);

  const fetchPOFData = useCallback(async (deliveryId: number) => {
    // Check if already cached
    if (pofData[deliveryId]) {
      return pofData[deliveryId];
    }
    
    try {
      const pofList = await listPOF(deliveryId);
      if (pofList) {
        setPofData(prev => ({ ...prev, [deliveryId]: pofList }));
      }
      return pofList;
    } catch (error) {
      console.error(`Error fetching POF for delivery ${deliveryId}:`, error);
      return null;
    }
  }, [pofData]);

  const deletePOFById = async (proofId: number, deliveryId: number) => {
    const result = await deletePOF(proofId);
    if (result) {
      message.success("POF deleted successfully");
      fetchPOFData(deliveryId);
    } else {
      message.error("Failed to delete POF");
    }
  };

  const removeProof = async (deliveryId: number, type: "pickup_proof" | "delivery_proof") => {
    await editDelivery({ id: deliveryId, [type]: null });
    message.info("Proof removed successfully");
    fetchDeliveries({ page: 1 });
  };

  const editPO = useCallback((po_id: any, params: any) => {
    editPurchaseOrder(po_id, params).then(async () => {
      fetchDeliveries({ page });
    });
  }, [page, fetchDeliveries]);

  useEffect(() => {
    fetchBranchMembers().then(setMembers);
    fetchUserDetails().then(setCurrentUser);
    // Only fetch vendors if needed
    if (search && search.length > 2) {
      getVendorsList();
    }
  }, [getVendorsList, search]);

  useEffect(() => {
    // When tab switches due to a search result, skip the tab fetch so search data is preserved.
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    setSearch("");
    fetchDeliveries({ page: 1, search: "" });
  }, [queryParams]);

  useEffect(() => {
    // Batch POF fetching with delay to avoid overwhelming API
    const timeoutId = setTimeout(() => {
      const deliveredIds = data
        .filter((item: any) => item?.id && item?.leg_status === "delivered")
        .map((item: any) => item.id);
      
      if (deliveredIds.length > 0) {
        // Only fetch POF for items not already cached
        const uncachedIds = deliveredIds.filter(id => !pofData[id]);
        if (uncachedIds.length > 0) {
          Promise.all(uncachedIds.map(fetchPOFData));
        }
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [data, fetchPOFData, pofData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    // State
    data, setData,
    loading, setLoading,
    page, setPage,
    total_pos, setTotalPos,
    search, setSearch,
    po_data, setPOData,
    members, activeTab, setActiveTab,
    created_at__gte, setCreated_at__gte,
    created_at__lte, setCreated_at__lte,
    dispatch_time__gte, setDispatchTime__gte,
    dispatch_time__lte, setDispatchTime__lte,
    deliveryTime__gte, setDeliveryTime__gte,
    deliveryTime__lte, setDeliveryTime__lte,
    created_by, setCreated_by,
    vendor_name, setVendor_name,
    tab_type, setTabType,
    dropdownVisible, setDropdownVisible,
    tripsDrawerVisible, setTripsDrawerVisible,
    deliveriesView, outstationView,
    etasView,
    attachDeliveryDrawerVisible, setAttachDeliveryDrawerVisible,
    attachPODDrawerVisible, setAttachPODDrawerVisible,
    attachLRDrawerVisible, setAttachLRDrawerVisible,
    warehouseDrawerVisible, setWarehouseDrawerVisible,
    showLoginextCSVUpload, setShowLoginextCSVUpload,
    delivery_location_drawer_visible, setDeliveryLocationDrawerVisible,
    eway_bill_drawer_visible, setEwayBillDrawerVisible,
    currentUser, vendors, vendor_id, setVendorId,
    deliveriesCount, deliveriesPage, setDeliveriesPage,
    itemsToView, setItemsToView,
    sortVal, setSortVal,
    pofData,
    etasSearch, setEtasSearch,
    porterLinkDrawerData, setPorterLinkDrawerData,
    dispatchTimeDrawerVisible, setDispatchTimeDrawerVisible,
    escalationDrawerData, setEscalationDrawerData,
    city, setCity,

    // Actions
    handleReset,
    handleSearchChange,
    fetchDeliveries,
    fetchPOFData,
    deletePOFById,
    removeProof,
    editPO,
    queryParams,
    tabParams
  };
};
